import isEqual from 'lodash/isEqual';
import { useCallback, useReducer } from 'react';

interface History<S> {
  snapshots: S[]; // ordered oldest ➜ newest
  index: number; // current snapshot pointer
}

type HistoryCommand =
  | { type: '__UNDO' }
  | { type: '__REDO' }
  | { type: '__JUMP'; index: number }
  | { type: '__RESET'; state: unknown };

type InternalAction<A> = A | HistoryCommand;

export function useReducerWithHistory<S, A extends { type: string }>(
  reducer: (state: S, action: A) => S,
  initialState: S,
  maxHistory = 50
) {
  const init = (): History<S> => {
    return { snapshots: [initialState], index: 0 };
  };

  const historyReducer = (
    history: History<S>,
    action: InternalAction<A>
  ): History<S> => {
    const actionType = (action as HistoryCommand).type;
    switch (actionType) {
      case '__UNDO': {
        if (history.index === 0) return history;
        return { ...history, index: history.index - 1 };
      }
      case '__REDO': {
        if (history.index >= history.snapshots.length - 1) return history;
        return { ...history, index: history.index + 1 };
      }
      case '__RESET': {
        return { snapshots: [(action as { state: S }).state], index: 0 };
      }
      case '__JUMP': {
        const index = (action as { index: number }).index;
        if (index < 0 || index >= history.snapshots.length) return history;
        return { ...history, index };
      }
      default: {
        // delegate to user reducer on current snapshot
        const currentState = history.snapshots[history.index] ?? initialState;
        const newPresent = reducer(currentState, action as A);

        if (isEqual(newPresent, currentState)) return history;

        // if caller wants to replace current snapshot, do so and return
        const replace = (action as { replacePreviousHistorySnapshot?: boolean })
          .replacePreviousHistorySnapshot;

        if (replace) {
          const newSnapshots = [...history.snapshots];
          newSnapshots[history.index] = newPresent;
          return { ...history, snapshots: newSnapshots };
        }

        // otherwise, push new snapshot; discard any future snapshots beyond current index
        const baseSnapshots = history.snapshots.slice(0, history.index + 1);
        const updatedSnapshots = [...baseSnapshots, newPresent];
        // trim older snapshots if exceeding maxHistory
        const excess = Math.max(0, updatedSnapshots.length - maxHistory);
        if (excess > 0) {
          updatedSnapshots.splice(0, excess); // drop oldest excess
        }
        const newIndex = updatedSnapshots.length - 1;
        return { snapshots: updatedSnapshots, index: newIndex };
      }
    }
  };

  const [historyState, dispatchInner] = useReducer(
    historyReducer,
    undefined,
    init
  );

  const undo = useCallback(() => dispatchInner({ type: '__UNDO' }), []);
  const redo = useCallback(() => dispatchInner({ type: '__REDO' }), []);
  const jumpTo = useCallback(
    (index: number) => dispatchInner({ type: '__JUMP', index }),
    []
  );

  const reset = useCallback(
    (state: S) => dispatchInner({ type: '__RESET', state }),
    []
  );

  const canUndo = historyState.index > 0;
  const canRedo = historyState.index < historyState.snapshots.length - 1;

  return {
    state: historyState.snapshots[historyState.index] ?? initialState,
    dispatch: dispatchInner as React.Dispatch<A>,
    undo,
    redo,
    jumpTo,
    reset,
    canUndo,
    canRedo,
    historyIndex: historyState.index,
  };
}
